/**
 * Eine Mail aus einer LISTE ziehen (User 01.11.2026: „Mail aus der Liste muss
 * man per Drag & Drop ziehen können, nicht erst, wenn man in der Mail ist“).
 *
 * Gemeinsam für alle Maillisten: Postfach-Seite (`MailView`), Schriftverkehr/
 * Kommunikation am Kontakt (`ContactMailView`). Die Nutzlast ist dieselbe wie
 * beim Griff im Mailfenster (`MailZiehGriff`) — Ziel sind Vorgangs-/To-do-
 * Karten und die Dokumente eines Kontakts (`postfachAblage.ts`).
 *
 * Ob gezogen werden darf, hängt am ZEIGER, nicht an der Fensterbreite: Bis
 * #2362 war die Zeile nur im Desktop-Layout (> 1024 px) ziehbar. Wer Edge
 * neben einem zweiten Fenster auf halbe Bildschirmbreite stellt — genau dann,
 * wenn man von einem Fenster ins andere ziehen will —, landet im Tablet- oder
 * Handy-Layout und konnte gar nicht ziehen. Ein reines Touch-Gerät (Handy,
 * iPad ohne Maus) zieht dagegen nicht: Dort wischt bzw. lange drückt die Zeile.
 */
import type * as React from "react";
import type { Email } from "../../services/graphMailService";
import { mailLinkSnapshot } from "./mailLinkSnapshot";
import { postfachMailNutzlast, ZIEHTYP_POSTFACH_MAIL } from "./postfachZug";

/** Hat das Gerät irgendeinen feinen Zeiger (Maus, Touchpad, Stift)? */
export function hatFeinenZeiger(fenster: Pick<Window, "matchMedia"> | undefined = typeof window !== "undefined" ? window : undefined): boolean {
    if (!fenster || typeof fenster.matchMedia !== "function") return true;
    try {
        // `any-pointer`: ein Windows-Laptop mit Touchscreen hat zusätzlich
        // Touchpad/Maus — dort soll gezogen werden. `pointer` allein meldet
        // nur den „primären“ Zeiger und wäre dort teils `coarse`.
        return fenster.matchMedia("(any-pointer: fine)").matches;
    } catch {
        return true;
    }
}

export type ZiehProps = {
    draggable?: true;
    onDragStart?: (e: React.DragEvent<HTMLElement>) => void;
};

/**
 * Drag-Props für eine Mailzeile. Ohne Postfach (noch nicht geladen) oder ohne
 * feinen Zeiger: keine — die Zeile verhält sich dann wie bisher.
 */
export function mailZeileZiehbar(
    mailbox: string | null | undefined,
    email: Email,
    feinerZeiger: boolean = hatFeinenZeiger(),
): ZiehProps {
    if (!mailbox || !feinerZeiger) return {};
    return {
        draggable: true,
        onDragStart: (e) => {
            e.dataTransfer.setData(ZIEHTYP_POSTFACH_MAIL, postfachMailNutzlast(mailLinkSnapshot(mailbox, email)));
            e.dataTransfer.effectAllowed = "copy";
        },
    };
}
